/**
 * How wide the paper a template asks for leaves its content, in CSS px.
 *
 * The approach: the harness lays the page out at exactly this width (the root element's width) before the
 * completeness check, the way the browser lays it out for printing. Content wider than the paper then sticks
 * out of the root element's box and fails as off the paper — so a page that only fits a wide preview frame
 * fails the same way it would be cut on paper, and a fluid page that fits paper isn't failed for a narrow frame.
 *
 * Each function is SELF-CONTAINED (embedded with `String(fn)`); `pageBoxPx` gets the length readers as arguments,
 * and `printableWidthPx` takes its result. They throw on a size or margin they can't read; the harness reports that
 * as a runtime failure the designer can fix.
 */

/** A CSS length in an `@page` rule, in px; `%` is of the paper's width. */
export function pageLengthPx(text: string, paperWidthPx: number): number {
	const PX_PER_INCH = 96;
	const PERCENT = 100;
	const UNIT_PX: Record<string, number> = {
		px: 1,
		in: PX_PER_INCH,
		cm: PX_PER_INCH / 2.54,
		mm: PX_PER_INCH / 25.4,
		q: PX_PER_INCH / 101.6,
		pt: PX_PER_INCH / 72,
		pc: 16,
	};
	const match = /^(-?\d*\.?\d+)([a-z%]*)$/i.exec(text.trim());
	if (match === null) throw new Error(`Can't read the @page length "${text}"`);
	const value = Number.parseFloat(match[1]);
	const unit = match[2].toLowerCase();
	if (unit === "%") return (value / PERCENT) * paperWidthPx;
	if (unit === "" && value === 0) return 0;
	if (!(unit in UNIT_PX)) throw new Error(`Can't read the @page length "${text}"`);
	return value * UNIT_PX[unit];
}

/** The paper width an `@page` `size` value gives, in px: named paper (turned for landscape), or its first length. */
export function pagePaperWidthPx(size: string, lengthPx: typeof pageLengthPx): number {
	const PX_PER_MM = 96 / 25.4;
	/** CSS named page sizes, [width, height] in mm, portrait. */
	const PAPER_MM: Record<string, [number, number]> = {
		a5: [148, 210],
		a4: [210, 297],
		a3: [297, 420],
		b5: [176, 250],
		b4: [250, 353],
		"jis-b5": [182, 257],
		"jis-b4": [257, 364],
		letter: [215.9, 279.4],
		legal: [215.9, 355.6],
		ledger: [279.4, 431.8],
	};
	// DEFAULT: with no paper named the printer's own is used; of A4 and Letter, the narrower one in each orientation.
	const UNNAMED_PAPER_MM = { portrait: 210, landscape: 279.4 };
	const words = size.trim().toLowerCase().split(/\s+/);
	const isLandscape = words.includes("landscape");
	const named = words.find((word) => word in PAPER_MM);
	if (named !== undefined) return (isLandscape ? PAPER_MM[named][1] : PAPER_MM[named][0]) * PX_PER_MM;
	if (words.every((word) => ["auto", "portrait", "landscape"].includes(word))) {
		return (isLandscape ? UNNAMED_PAPER_MM.landscape : UNNAMED_PAPER_MM.portrait) * PX_PER_MM;
	}
	return lengthPx(words[0], 0);
}

export type PageReaders = { lengthPx: typeof pageLengthPx; paperWidthPx: typeof pagePaperWidthPx };

export type PageMarginsPx = { top: number; right: number; bottom: number; left: number };

/** The paper's width and the page margins a template asks for, in px. */
export type PageBoxPx = { paperWidthPx: number; margins: PageMarginsPx };

/**
 * The paper and margins of the page's `@page` rules. Conservative where the template is ambiguous: across several
 * rules (`:first`, named pages) it takes the narrowest paper and, per side, the widest margin declared; with no size
 * declared, it reads as `size: auto`.
 */
export function pageBoxPx(doc: Document, readers: PageReaders): PageBoxPx {
	const PAGE_RULE_TYPE = 6; // CSSRule.PAGE_RULE, written out so the function needs no global
	// DEFAULT: with no margin set the browser's own applies; Firefox's half inch is the widest of the major browsers'.
	const UNSET_MARGIN_PX = 48;
	const pageRules = (rules: CSSRuleList): CSSPageRule[] =>
		Array.from(rules).flatMap((rule) => {
			// The type check makes the cast safe; grouping rules (@layer, @supports) are searched too.
			if (rule.type === PAGE_RULE_TYPE) return [rule as CSSPageRule];
			return "cssRules" in rule ? pageRules((rule as CSSGroupingRule).cssRules) : [];
		});
	const rules = Array.from(doc.styleSheets).flatMap((sheet) => pageRules(sheet.cssRules));
	const declared = (property: string): string[] => rules.map((rule) => rule.style.getPropertyValue(property)).filter((value) => value !== "");
	const sizes = declared("size");
	const paperWidthPx = Math.min(...(sizes.length > 0 ? sizes : ["auto"]).map((size) => readers.paperWidthPx(size, readers.lengthPx)));
	// A percentage is read against the paper's width for every side: an approximation for top and bottom, which CSS
	// reads against its height, that only matters for printing's padding, never for the width the page is laid out at.
	const widestMarginPx = (side: keyof PageMarginsPx): number => {
		const margins = declared(`margin-${side}`);
		return margins.length === 0 ? UNSET_MARGIN_PX : Math.max(...margins.map((margin) => readers.lengthPx(margin, paperWidthPx)));
	};
	return {
		paperWidthPx,
		margins: { top: widestMarginPx("top"), right: widestMarginPx("right"), bottom: widestMarginPx("bottom"), left: widestMarginPx("left") },
	};
}

/** The page's printable width: its paper less the left and right margins. */
export function printableWidthPx(box: PageBoxPx): number {
	const widthPx = box.paperWidthPx - box.margins.left - box.margins.right;
	if (!(widthPx > 0)) throw new Error("The @page margins leave no room on the paper");
	return widthPx;
}
